import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Switch,
  TableProps,
} from "antd";

import dayjs from "dayjs";
import { FormInstance } from "rc-field-form";
import { useEffect, useMemo, useState } from "react";
import AddIcon from "../../../../assets/icons/AddIcon";
import DateIcon from "../../../../assets/icons/DateIcon";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import SelectDropdownLgIcon from "../../../../assets/icons/SelectDropdownLgIcon";
import SortIcon from "../../../../assets/icons/SortIcon";
import CustomAmountModal from "../../../../components/Settings/Payroll/CustomAmountModal";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import CustomModal from "../../../../components/ui/CustomModal";
import CustomTable from "../../../../components/ui/CustomTable";
import colors from "../../../../constants/colors";
import { useGetDepartmentsQuery } from "../../../../features/department/departmentApi";
import { useGetEmployeesQuery } from "../../../../features/employee/employeeApi";
import { useGetPayrollSettingQuery } from "../../../../features/payrollSetting/payrollSettingApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import { toast } from "../../../../lib/toast";
import { EmployeeData, ModalComponentProps } from "../../../../types";
import { capitalize } from "../../../../utils/string";
import {
  addKeyInArray,
  convertToSnakeCase,
  selectArrayOption,
  selectSearchOption,
} from "../../../../utils/utils";
import AdditionsAddEmployeeModal from "./AdditionsAddEmployeeModal";
import { PayComponentModalType } from "./PayComponents";

type DeductionsModalType = {
  handleDataList: (data: any, type: "ADD" | "EDIT" | "DELETE") => void;
} & ModalComponentProps<any, PayComponentModalType>;

export default function DeductionsModal({
  handleClose,
  type,
  handleDataList,
  data,
  ...rest
}: DeductionsModalType) {
  const [form] = Form.useForm();
  const isTaxable = Form.useWatch("is_taxable", form);
  const {
    open,
    handleClose: handleRemoveClose,
    handleOpen,
  } = useModal<
    number,
    "CONFIRM_DELETE" | "CONFIRM_PACKAGE" | "CUSTOM_AMOUNT_MODAL"
  >();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const compTitle = Form.useWatch("title", form);

  const amountData = {
    title: compTitle,
    name: convertToSnakeCase(compTitle),
    is_taxable: isTaxable ? 1 : 0,
    editId: data?.id || undefined,
  };

  const onFinish = (values: any) => {
    const { title, is_taxable, ...rest } = values;

    const findDEffect = rest?.package?.find(
      (item: any) =>
        item?.effective_for === "employee" && !item?.targetEmployee?.length
    );

    if (findDEffect) {
      toast("error", "Please select at least one employee");
      return;
    }

    const newData = {
      ...rest,
      title,
      name: convertToSnakeCase(title),
      package: rest?.package?.map((item: any) => ({
        ...item,
        key: convertToSnakeCase(item?.name),
        is_active: item?.is_active ? 1 : 0,
        is_percentage: item?.on_what === "fixed_amount" ? 0 : 1,
        amount: Number(
          item?.on_what === "fixed_amount"
            ? item?.amount
            : item?.amountPercentage
        ),
        amountPercentage: undefined,
        schedule_date: item?.schedule_date
          ? item?.schedule_date?.month() + 1
          : null,
        schedule_year: item?.schedule_date
          ? item?.schedule_date?.year()
          : undefined,
        // schedule_date: item?.schedule_date
        //   ? item?.schedule_date?.format(dbDateFormat)
        //   : null,
        target:
          item?.effective_for === "department"
            ? item?.targetDepartment
            : item?.effective_for === "employee"
            ? item?.targetEmployee
            : null,
        targetDepartment: undefined,
        targetEmployee: undefined,
      })),
    };

    if (type === "DEDUCTION_ADD") {
      handleDataList(newData, "ADD");
      toast("success", "Pay components successfully added!");
      handleClose();
    } else if (type === "DEDUCTION_EDIT") {
      handleDataList(
        {
          ...newData,
          id: data?.id,
        },
        "EDIT"
      );
      toast("success", "Pay components successfully updated!");
      handleClose();
    }
  };

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        title: data?.title,
        package: data?.package?.map((item: any) => ({
          id: item?.id,
          effective_for: item?.effective_for ?? "global",
          is_active: item?.is_active ? true : false,
          amount: Number(item?.amount) ?? undefined,
          amountPercentage:
            item?.on_what !== "fixed_amount" ? Number(item?.amount) : undefined,
          calculation_type: item?.calculation_type ?? undefined,
          is_percentage: item?.is_percentage ?? 0,
          name: item?.name ?? undefined,
          on_what: item?.on_what ?? "fixed_amount",
          periodic_schedule: item?.periodic_schedule ?? 1,
          schedule_date: item?.schedule_date
            ? dayjs(
                `${item?.schedule_year || new Date().getFullYear()}-${
                  item?.schedule_date
                }-01`
              )
            : undefined,
          // schedule_date: item?.schedule_date
          //   ? dayjs(item?.schedule_date)
          //   : undefined,
          schedule_type: item?.schedule_type ?? "periodically",
          targetDepartment:
            item?.effective_for === "department" ? item?.target : undefined,
          targetEmployee:
            item?.effective_for === "employee" ? item?.target : undefined,
        })),
      });
    }
  }, [data, form]);

  return (
    <CustomModal
      title={type === "DEDUCTION_EDIT" ? "Update Package" : "Add New Package"}
      {...rest}
      handleClose={handleClose}
      maskClosable={false}
      footer={null}
      width={600}
    >
      {open?.type === "CONFIRM_DELETE" ? (
        <ConfirmModal
          open={open?.open}
          handleOk={() => {
            handleDataList(data, "DELETE");
            handleRemoveClose();
            handleClose();
          }}
          handleClose={handleRemoveClose}
          confirmTitle="Wait!"
          questionText={`Are you sure you want to delete the deduction component "${
            data?.title || data?.value
          }" ?`}
          okText="Yes"
          cancelText="No"
        />
      ) : null}

      {open.type === "CUSTOM_AMOUNT_MODAL" && (
        <CustomAmountModal
          open={open?.open}
          data={open?.data}
          type={type}
          amountData={amountData}
          handleDataList={handleDataList}
          handleClose={() => {
            handleClose();
          }}
        />
      )}

      <Form
        className="custom-form"
        layout="vertical"
        requiredMark="optional"
        size="large"
        form={form}
        onFinish={onFinish}
      >
        <div className="flex justify-between items-center mb-2">
          <Button
            type="default"
            onClick={() =>
              handleOpen({
                type: "CUSTOM_AMOUNT_MODAL",
                data: data?.package?.length || 0,
              })
            }
            size="middle"
            disabled={!compTitle}
            className="disabled:!bg-white"
          >
            Custom Amount
          </Button>

          {type === "DEDUCTION_EDIT" && typeof data?.id === "number" ? (
            <Button
              icon={
                <span className="text-dark-highlight">
                  <DeleteIcon />
                </span>
              }
              className="action-button"
              onClick={() => handleOpen({ type: "CONFIRM_DELETE" })}
            />
          ) : null}
        </div>
        <Form.Item
          name="title"
          label="Component Title"
          className="mb-4"
          rules={[{ required: true, message: "Please enter component title" }]}
        >
          <Input placeholder="Type component name..." />
        </Form.Item>

        <Form.List
          initialValue={[
            {
              is_active: true,
              calculation_type: "fix_pay_amount",
              on_what: "fixed_amount",
              schedule_type: "periodically",
              periodic_schedule: 1,
              effective_for: "global",
            },
          ]}
          name="package"
        >
          {(fields, { add, remove }) => {
            return (
              <>
                {fields.map((field, index) => {
                  return (
                    <div className="flex flex-col gap-2 mb-3" key={field.key}>
                      {/* {open?.type === "CONFIRM_PACKAGE" ? (
                        <ConfirmModal
                          open={open?.open}
                          handleOk={() => {
                            if (open?.data) {
                              remove(open?.data);
                              handleRemoveClose();
                            }
                          }}
                          handleClose={handleRemoveClose}
                          confirmTitle="Wait!"
                          questionText={`Are you sure you want to delete this package??`}
                          okText="Yes"
                          cancelText="No"
                        />
                      ) : null} */}

                      <p className="!text-[18px] font-semibold">#{index + 1}</p>

                      <div className="flex items-center justify-between gap-1">
                        <Form.Item
                          name={[index, "name"]}
                          className="mb-0 w-[70%]"
                          rules={[
                            {
                              required: true,
                              message: "Please enter package name",
                            },
                          ]}
                          initialValue={`Package ${index + 1}`}
                        >
                          <Input placeholder="Package name" />
                        </Form.Item>
                        <PackageActiveInactive index={index} form={form} />
                        {fields?.length > 1 ? (
                          <Button
                            icon={<DeleteIcon />}
                            className="action-button"
                            // onClick={() =>
                            //   handleOpen({
                            //     type: "CONFIRM_PACKAGE",
                            //     data: field.name,
                            //   })
                            // }
                            onClick={() => {
                              setDeleteIndex(index);
                              setConfirmOpen(true);
                            }}
                          />
                        ) : null}
                      </div>

                      <ul className="mb-0">
                        <li className="py-2 flex items-center justify-between gap-4">
                          <h6 className="min-w-[130px] text-header text-base font-medium">
                            Calculation type:
                          </h6>
                          <Form.Item
                            name={[index, "calculation_type"]}
                            className="flex-1 flex items-center gap-6 mb-0"
                            initialValue={"fix_pay_amount"}
                          >
                            <Radio.Group className="custom-radio text-header text-sm font-medium">
                              {typeListCalculation?.map((val) => (
                                <Radio
                                  className="text-header text-base font-medium"
                                  value={val.value}
                                  key={val.value}
                                  name=""
                                >
                                  {capitalize(val.label)}
                                </Radio>
                              ))}
                            </Radio.Group>
                          </Form.Item>
                        </li>
                      </ul>

                      <CalculateType index={index} form={form} />

                      <ul className="mb-2">
                        <li className="py-0 flex items-center justify-between">
                          <h6 className="min-w-[130px] text-header text-base font-medium">
                            Schedule type:
                          </h6>
                          <Form.Item
                            name={[index, "schedule_type"]}
                            className="mb-0 flex-1 flex items-center gap-6"
                            initialValue={"periodically"}
                          >
                            <Radio.Group className="custom-radio text-header text-sm font-medium">
                              {typeListSchedule?.map((val) => (
                                <Radio value={val.value} key={val.value}>
                                  {capitalize(val.label)}
                                </Radio>
                              ))}
                            </Radio.Group>
                          </Form.Item>
                        </li>
                      </ul>

                      <ScheduleType index={index} form={form} />

                      <ul>
                        <li className="flex items-center justify-between">
                          <h6 className="min-w-[130px] text-header text-sm font-medium">
                            Effective for:
                          </h6>
                          <div className="flex-1 flex items-center gap-6">
                            <Form.Item
                              name={[index, "effective_for"]}
                              className="mb-0"
                              initialValue={"global"}
                            >
                              <Radio.Group className="custom-radio text-header text-sm font-medium">
                                {typeListEffective?.map((val) => (
                                  <Radio value={val?.value} key={val?.value}>
                                    {capitalize(val?.label)}
                                  </Radio>
                                ))}
                              </Radio.Group>
                            </Form.Item>
                          </div>
                        </li>
                      </ul>

                      <EffectiveFor index={index} form={form} />
                    </div>
                  );
                })}

                <ConfirmModal
                  open={confirmOpen}
                  handleOk={() => {
                    if (deleteIndex !== null) {
                      remove(deleteIndex);
                      setDeleteIndex(null);
                      setConfirmOpen(false);
                    }
                  }}
                  handleClose={() => {
                    setDeleteIndex(null);
                    setConfirmOpen(false);
                  }}
                  confirmTitle="Wait!"
                  questionText="Are you sure you want to delete this package?"
                  okText="Yes"
                  cancelText="No"
                />

                <div className="flex justify-end">
                  <Button
                    size="middle"
                    type="primary"
                    icon={<AddIcon />}
                    className="!bg-[#001652] sm-button !px-3"
                    onClick={() => add()}
                  >
                    Add More
                  </Button>
                </div>
              </>
            );
          }}
        </Form.List>

        <div className="flex justify-start gap-2 mt-4">
          <Button className="!w-[125px]" size="middle" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="middle"
            className="!w-[125px]"
            htmlType="submit"
            // onClick={handleClose}
          >
            {type === "DEDUCTION_EDIT" ? "Update" : "Save"}
          </Button>
        </div>
      </Form>
    </CustomModal>
  );
}

type ListComponentType = {
  index: number;
  form: FormInstance<any>;
};

const PackageActiveInactive = ({ index, form }: ListComponentType) => {
  const isActive = Form.useWatch(["package", index, "is_active"], form);

  return (
    <div className="flex items-center gap-1.5">
      <Form.Item
        name={[index, "is_active"]}
        valuePropName="checked"
        className="mb-0"
        initialValue={true}
      >
        <Switch />
      </Form.Item>
      <label className="text-sm font-medium text-header">
        {isActive ? "Active" : "Inactive"}
      </label>
    </div>
  );
};

const CalculateType = ({ index, form }: ListComponentType) => {
  const calculationType = Form.useWatch(
    ["package", index, "calculation_type"],
    form
  );
  const onWhat = Form.useWatch(["package", index, "on_what"], form);

  const {
    data: salaryData,
    isLoading,
    isError,
    error,
  } = useGetPayrollSettingQuery();
  useError(isError, error, "Get salary breakdown data fail");

  const [sData, setSData] = useState<any>();

  useEffect(() => {
    if (salaryData) {
      setSData([
        ...(typeListCalculationSelect || []),
        ...(salaryData?.data?.payroll_setting?.salary_breakdown
          ?.filter((item) => item?.is_active)
          ?.map((item) => ({
            label: `% of ${item?.title}`,
            value: item?.id?.toString(),
          })) || []),
      ]);
    }
  }, [salaryData]);

  return calculationType === "fix_pay_amount" ? (
    <div className="grid grid-cols-2 gap-2.5 border-b border-[#D7DFE9] pb-3">
      <Form.Item
        name={[index, "on_what"]}
        label="Select Component"
        className="mb-2"
        rules={[
          {
            required: true,
            message: "Please select component",
          },
        ]}
        initialValue={"fixed_amount"}
      >
        <Select
          className="w-full"
          suffixIcon={<SelectDropdownLgIcon />}
          options={sData}
          loading={isLoading}
        />
      </Form.Item>
      {onWhat === "fixed_amount" ? (
        <>
          <Form.Item
            validateFirst
            name={[index, "amount"]}
            label="Enter Amount"
            className="mb-2"
            rules={[
              {
                required: true,
                message: "Please enter amount",
              },
              {
                type: "number",
                min: 0,
                message: "Amount cannot be negative",
              },
              {
                validator: async (_: any, value: string) => {
                  if (!value) {
                    return Promise.resolve();
                  }

                  if (value?.toString().length > 9) {
                    return Promise.reject(
                      new Error("Amount cannot exceed 9 digits")
                    );
                  }
                },
              },
            ]}
          >
            <InputNumber
              placeholder="0.00"
              className="w-full"
              prefix="BDT"
              onKeyDown={(e) => {
                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                  e.preventDefault();
                }
              }}
              onWheel={(e) => {
                e.preventDefault();
                (e.target as HTMLElement).blur();
              }}
              keyboard={false}
              controls={false}
            />
          </Form.Item>
        </>
      ) : (
        <>
          <Form.Item
            validateFirst
            name={[index, "amountPercentage"]}
            label="Enter Percentage"
            className="mb-2"
            rules={[
              {
                required: true,
                message: "Please enter percentage",
              },
              {
                type: "number",
                min: 0,
                message: "Percentage cannot be negative",
              },
              {
                type: "number",
                max: 100,
                message: "Percentage can not be more than 100%",
              },
            ]}
          >
            <InputNumber
              placeholder="Value %"
              type="number"
              className="w-full"
              onKeyDown={(e) => {
                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                  e.preventDefault();
                }
              }}
              onWheel={(e) => {
                e.preventDefault();
                (e.target as HTMLElement).blur();
              }}
              controls={false}
            />
          </Form.Item>
        </>
      )}
    </div>
  ) : null;
};

const ScheduleType = ({ index, form }: ListComponentType) => {
  const scheduleType = Form.useWatch(["package", index, "schedule_type"], form);

  return (
    <div className="grid grid-cols-2 gap-2 border-b border-[#D7DFE9] pb-4">
      {scheduleType === "periodically" ? (
        <>
          <div className="flex items-center gap-3 text-header text-base font-medium">
            <span>Every</span>
            <Form.Item
              validateFirst
              name={[index, "periodic_schedule"]}
              className="mb-0"
              rules={[
                {
                  required: true,
                  message: "Enter pay period",
                },
                {
                  pattern: /^[0-9]+$/,
                  message: "Only numeric value allowed",
                },
                {
                  type: "number",
                  min: 1,
                  message: "Only numeric value allowed",
                },
                {
                  type: "number",
                  max: 100,
                  message: "Value can not be more than 100",
                },
              ]}
              initialValue={1}
            >
              <InputNumber type="number" className="w-16" size="middle" />
            </Form.Item>
            <span>Pay Period</span>
          </div>

          {/* error message */}
        </>
      ) : (
        <>
          <div className="flex justify-between items-center text-header text-base font-medium">
            <span className="min-w-[130px]">Select Month</span>
            <Form.Item
              name={[index, "schedule_date"]}
              className="mb-0"
              rules={[
                {
                  required: true,
                  message: "Please select month",
                },
              ]}
            >
              <DatePicker
                format="MMMM YYYY"
                suffixIcon={<DateIcon />}
                picker="month"
                size="middle"
                className="clear-button-hide min-w-[160px]"
                // popupClassName="calender-header-hide"
              />
            </Form.Item>
          </div>
        </>
      )}
    </div>
  );
};

const EffectiveFor = ({ index, form }: ListComponentType) => {
  const [selectedData, setSelectedData] = useState<number[]>([]);
  const { open, handleClose, handleOpen } = useModal<EmployeeData[]>();
  const effectiveFor = Form.useWatch(["package", index, "effective_for"], form);
  const [employeeList, setEmployeeList] = useState<EmployeeData[]>([]);

  const targetList = employeeList?.map((item) => item?.id);

  const handleRemoveEmployee = () => {
    const updatedList = employeeList?.filter(
      (item) => !selectedData?.some((el) => el === item?.id)
    );
    setEmployeeList(updatedList);
    setSelectedData([]);
  };

  const {
    data: departmentData,
    isError: isDepartmentError,
    error: departmentError,
  } = useGetDepartmentsQuery(
    {
      params: { limit: 0, skip: 0, is_published: 1 },
    },
    {
      skip: effectiveFor !== "department",
    }
  );
  const { isLoading, isError, error, data } = useGetEmployeesQuery({
    params: {
      search_type: undefined,
      status: "active",
    },
  });
  useError(isError, error, "Get employee data fail");
  useError(isDepartmentError, departmentError, "Get department fail");
  const stableTargetList = useMemo(() => targetList, [targetList]);

  useEffect(() => {
    if (!isLoading) {
      const prevValue = form.getFieldValue([
        "package",
        index,
        "targetEmployee",
      ]);

      const updatedList = data?.data?.employees?.filter((item) =>
        prevValue?.some((el: number) => el === item?.id)
      ) as EmployeeData[];

      setEmployeeList(updatedList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data?.employees]);

  useEffect(() => {
    if (!stableTargetList?.length) return;

    const prevValue = form.getFieldValue(["package", index, "targetEmployee"]);

    if (JSON.stringify(prevValue) !== JSON.stringify(stableTargetList)) {
      form.setFieldsValue({
        package: { [index]: { targetEmployee: stableTargetList } },
      });
    }
  }, [form, index, stableTargetList]);

  const columns: TableProps<EmployeeData>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: () => (
        <span className="whitespace-no-wrap block">Employee Id</span>
      ),
      dataIndex: "employee_id",
      sorter: (a, b) =>
        Number(a?.employee_id ?? 0) - Number(b?.employee_id ?? 0),
      sortIcon: () => <SortIcon />,
      render: (val) => val ?? "n/a",
    },
    {
      title: () => (
        <span className="whitespace-no-wrap block">Employee Name</span>
      ),
      dataIndex: "member",
      render: (val) => val?.profile?.name ?? "n/a",
      width: 200,
    },
    {
      title: () => <span className="whitespace-no-wrap block">Email</span>,
      dataIndex: "member",
      render: (val) => val?.profile?.email ?? "n/a",
    },
  ];

  if (effectiveFor === "department") {
    return (
      <Form.Item
        name={[index, "targetDepartment"]}
        label="Department"
        className="mb-2"
        rules={[
          {
            required: true,
            message: "Please select department",
          },
        ]}
      >
        <Select
          placeholder="Select"
          size="large"
          allowClear
          showSearch
          filterOption={selectSearchOption}
          options={selectArrayOption(
            departmentData?.data?.departments || [],
            "name",
            "id"
          )}
          suffixIcon={<SelectDropdownLgIcon />}
          mode="multiple"
        />
      </Form.Item>
    );
  }

  if (effectiveFor === "employee") {
    return (
      <div>
        {open.type === "VIEW" && (
          <AdditionsAddEmployeeModal
            open={open.open}
            data={open?.data}
            handleClose={handleClose}
            onChange={(employees) => setEmployeeList(employees)}
          />
        )}

        <div className="flex justify-between items-center !bg-[#F0F3F9] pt-1 pb-1 pl-2 pr-2 rounded-md mb-2">
          <div className="text-[16px] font-semibold text-[#2D3643]">
            Deductions
          </div>
          <div>
            {selectedData?.length ? (
              <Button
                className="outline-button-primary border-none bg-transparent"
                size="small"
                icon={<DeleteIcon />}
                onClick={handleRemoveEmployee}
              >
                Remove Employee
              </Button>
            ) : (
              <Button
                className="outline-button-primary border-none bg-transparent"
                size="small"
                icon={<AddIcon color={colors.darkHighlight} />}
                onClick={() => {
                  handleOpen({ type: "VIEW", data: employeeList });
                }}
              >
                Add Employee
              </Button>
            )}
          </div>
        </div>

        <CustomTable
          style={{ border: "none" }}
          columns={columns}
          dataSource={addKeyInArray(employeeList)}
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: selectedData,
            onChange: (key) => {
              setSelectedData(key as number[]);
            },
            columnWidth: 40,
          }}
        />
      </div>
    );
  }

  return null;
};

// type AddMoreButtonType = {
//  add: () => void
// } & ListComponentType;

// const AddMoreButton = ({form, index, add}: AddMoreButtonType) => {
//   const effectiveFor = Form.useWatch(["package", index, "effective_for"], form);
//   const targetEmployee = Form.useWatch(["package", index, "targetEmployee"], form);

//   if(effectiveFor === "global" && !targetEmployee?.length ) {
//     toast("error", "Please select at least one employee")
//     return;
//   }

//   return  <div className="flex justify-end">
//   <Button
//     size="middle"
//     type="primary"
//     icon={<AddIcon />}
//     className="!bg-[#001652] sm-button !px-3"
//     onClick={() => add()}
//   >
//     Add More
//   </Button>
// </div>
// }

const typeListCalculation = [
  { label: "Fixed Pay Amount", value: "fix_pay_amount" },
  // { label: "Variable Amount", value: "variable_amount" },
];

const typeListCalculationSelect = [
  { label: "Fixed Amount", value: "fixed_amount" },
  { label: "% of Gross Salary", value: "gross_salary" },
  // { label: "% of Basic Salary", value: "basic_alary" },
  // { label: "% of House Rent", value: "house_rent" },
  // { label: "% of Medical Allowance", value: "medical_allowance" },
  // { label: "% of Conveyance", value: "conveyance" },
];
const typeListSchedule = [
  { label: "Periodically", value: "periodically" },
  { label: "Fixed Date", value: "fixed_date" },
];
const typeListEffective = [
  { label: "Global", value: "global" },
  { label: "Department", value: "department" },
  { label: "Employee", value: "employee" },
];
